/**
 * The kiosk's own Redux state — the ONLY genuinely new state the feature adds.
 *
 * Lives in packages/core rather than in each app's `slices/` folder on purpose. Both
 * apps register this same reducer under the key `kioskStore`, so there is one
 * definition and no chance of the two drifting. The two stores already differ by four
 * keys (web `commonReducer`/`globalLoaderReducer`, mobile `catalogMapStore`/`mySlice`)
 * and the architecture doc is explicit: do not add a fifth divergence.
 *
 * NOT PERSISTED. Both apps' redux-persist configs are opt-in whitelists, so simply
 * leaving `kioskStore` out of them is enough. A kiosk that restarts mid-order must come
 * back to IDLE, not resume a stranger's cart.
 *
 * Everything a customer touches lives here — including catalog UI state like the search
 * term and tag filter — so that ONE reset clears the whole session. See
 * `kiosk/session` for the reset itself; it must also clear `selectedChannelData` in
 * saleDataStore, which `resetSaleData()` deliberately preserves.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { KIOSK_ROUTES, KioskRoute } from "../../../kiosk/navigation/routes";
// Mode lives in the PERSISTED slice (it must survive a restart); the session lives here
// and must not. Importing the action lets this slice react to mode being switched on.
import { setKioskEnabled } from "../kioskConfigSlice";
// The tag is a property of the ITEM, so it is defined by the ordering domain and the
// kiosk's filter state just references it. kiosk → ordering is the allowed direction.
import type { DietaryTag } from "../../../catalog/types";

export interface KioskState {
  /**
   * The staff session behind the kiosk is over — the backend answered `IDLE_TIMEOUT` /
   * `UNAUTHENTICATED_USER` / a licence or version error.
   *
   * A FLAG, not a navigation: the app's forced-logout path sets this instead of clearing
   * to the login screen, and `KioskApp` turns it into a route change. That keeps the
   * POS-side change identical on web and mobile, and keeps every navigation inside the
   * kiosk's own nav seam.
   *
   * Survives `resetKioskSession` — an idle timeout must not walk a customer back to a
   * kiosk that cannot submit an order. Cleared only by re-entering kiosk mode.
   *
   * NOT persisted: after a restart it self-corrects on the first failing call.
   */
  sessionExpired: boolean;
  /**
   * Mirror of the platform router's current route, written in exactly ONE place (the
   * navigator wrapper in packages/ui/kiosk/navigation). It exists so core — timeouts,
   * guards, analytics — can read location without importing a router.
   */
  route: KioskRoute;

  /** null = idle. Any other value means a customer session is in progress. */
  sessionId: string | null;
  startedAt: number | null;
  /** Bumped by ANY touch, captured once in KioskApp. Drives the idle timer. */
  lastInteractionAt: number;

  /** Chosen service → resolves to a channel. Cleared on reset (see landmine 6). */
  serviceId: string | null;

  /**
   * The customer has ANSWERED the contact prompt — by giving a number or by skipping it.
   *
   * Not "has a phone number": the number itself lives on the sale (`customerInfo`), and a
   * `Optional` skip leaves no trace there at all. Without this flag the modal would reopen
   * every time the customer walked back into the cart, having already declined once.
   *
   * Session state, so it clears with `resetKioskSession` for free — customer B must be asked
   * even though customer A said no.
   */
  contactPromptResolved: boolean;

  payAtCounter: boolean;
  /** Which sale the customer placed. */
  submittedSaleMid: string | null;
  /**
   * What the success screen shows, COPIED off the placed sale rather than read from it.
   *
   * 🔴 **This is the one place a copy is right, and the reason is the RESET.** Both values
   * are the sale's own (`orderNumber` and `url`, "Sale URL, used for QR code printing" —
   * `models/sale`), and reading them straight off `saleDataStore` is the obvious thing to
   * do. But the success screen OUTLIVES that sale: the flow's normal end is a 20s pause on
   * this screen and then `resetSession`, which clears the sale as a privacy control (§2) —
   * so a screen reading through to `saleDataStore` blanks its own order number the instant
   * the customer is done with it, and any earlier reset (a staff exit, a session teardown)
   * does the same mid-read.
   *
   * They are cleared with the rest of the session, which is what `resetKioskSession` is
   * for — so this copy lasts exactly as long as the screen that shows it.
   */
  orderToken: string | null;
  receiptUrl: string | null;

  // ---- catalog UI state: here so one reset clears it ----
  selectedCategoryId: string | null;
  activeTag: DietaryTag | null;
  searchTerm: string;

  /**
   * The language THIS CUSTOMER picked, overriding the merchant's default. `null` = they did
   * not pick one.
   *
   * It lives here rather than in `kioskConfigStore` precisely so it is not persisted and is
   * cleared by `resetKioskSession` with everything else: the next customer must meet the
   * merchant's default, not the last customer's choice. §2 calls the reset a privacy control,
   * and a stranger's language left on the attract screen is the same class of leak as their
   * cart.
   *
   * Never read it directly — `selectKioskLanguage` in `kiosk/language` resolves it against the
   * config default, and is the only file that knows both keys.
   */
  language: string | null;
}

/*
 * ⚠️ DO NOT ADD `selectedChannelData`, `catalog` or `categories` here — all three existed
 * on this slice briefly and were removed when DP-7554 merged in.
 *
 *   · `selectedChannelData` was a MIRROR of `saleDataStore.selectedChannelData`. The
 *     channel has one home, read through `channel`'s `selectChannel`. A second
 *     copy is precisely the bug that module exists to kill — the POS has three different
 *     object shapes all called "the channel", which is how `groupId` goes missing and
 *     group-priced merchants mis-price.
 *   · `catalog` and `categories` were DERIVED data round-tripped through Redux.
 *     `catalog`'s `prepareCatalogForChannel` memoises them by `channelId` with
 *     ONE invalidation call (`clearPreparedCatalogs`); the POS's habit of storing derived
 *     data in a slice is what turns its cache invalidation into a scatter of `clear*`
 *     dispatches. Read the comment on `preparedCatalogs` before reaching for a slice.
 */

const initialState: KioskState = {
  sessionExpired: false,
  route: KIOSK_ROUTES.IDLE,
  sessionId: null,
  startedAt: null,
  lastInteractionAt: 0,
  serviceId: null,
  contactPromptResolved: false,
  payAtCounter: false,
  submittedSaleMid: null,
  orderToken: null,
  receiptUrl: null,
  selectedCategoryId: null,
  activeTag: null,
  searchTerm: "",
  language: null,
};

export const kioskSlice = createSlice({
  name: "kiosk",
  initialState,
  reducers: {
    /**
     * Called by the app's forced-logout path INSTEAD of logging out, when kiosk mode is
     * on. No toast, no login redirect — `KioskApp` routes to the dead-end screen.
     */
    markSessionExpired: (state) => {
      state.sessionExpired = true;
    },
    setKioskRoute: (state, action: PayloadAction<KioskRoute>) => {
      state.route = action.payload;
    },
    startKioskSession: (state, action: PayloadAction<{ sessionId: string; now: number }>) => {
      state.sessionId = action.payload.sessionId;
      state.startedAt = action.payload.now;
      state.lastInteractionAt = action.payload.now;
    },
    noteInteraction: (state, action: PayloadAction<number>) => {
      state.lastInteractionAt = action.payload;
    },
    setKioskService: (state, action: PayloadAction<string | null>) => {
      state.serviceId = action.payload;
    },
    /**
     * The contact prompt has been answered. Both outcomes set it — a skip is an answer.
     *
     * Takes a boolean rather than being a bare `markContactPromptResolved`, so the "Change
     * number" affordance on the cart can put the customer back into an unanswered state
     * without a second action that means almost the same thing.
     */
    setContactPromptResolved: (state, action: PayloadAction<boolean>) => {
      state.contactPromptResolved = action.payload;
    },
    setPayAtCounter: (state, action: PayloadAction<boolean>) => {
      state.payAtCounter = action.payload;
    },
    setOrderResult: (
      state,
      action: PayloadAction<{
        saleMid: string | null;
        token: string | null;
        receiptUrl: string | null;
      }>,
    ) => {
      state.submittedSaleMid = action.payload.saleMid;
      state.orderToken = action.payload.token;
      state.receiptUrl = action.payload.receiptUrl;
    },
    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategoryId = action.payload;
    },
    setActiveTag: (state, action: PayloadAction<DietaryTag | null>) => {
      state.activeTag = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    /** The customer's language choice. `null` hands them back to the merchant's default. */
    setKioskLanguage: (state, action: PayloadAction<string | null>) => {
      state.language = action.payload;
    },
    /**
     * Clears the customer's session. A timeout returns the device to the attract screen;
     * it does not drop out of kiosk mode (that flag lives in `kioskConfigStore`, so it is
     * untouched here and survives a restart) and it does not clear `sessionExpired` — a
     * dead staff session must not walk the next customer into a kiosk that cannot submit.
     *
     * This is only the kiosk half of a reset. The sale/customer/loyalty half runs
     * through `resetSaleData()`; both are called by the single reset in kiosk/session
     * so the timeout path and the "start over" button cannot diverge.
     */
    resetKioskSession: (state) => ({
      ...initialState,
      sessionExpired: state.sessionExpired,
    }),
  },
  extraReducers: (builder) => {
    // Entering kiosk mode starts clean: staff have just logged in again, so whatever
    // killed the last session is resolved. `sessionExpired` is not persisted, but this
    // is what clears it within a single app run.
    builder.addCase(setKioskEnabled, (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.sessionExpired = false;
      }
    });
  },
});

export const {
  markSessionExpired,
  setKioskRoute,
  startKioskSession,
  noteInteraction,
  setKioskService,
  setContactPromptResolved,
  setPayAtCounter,
  setOrderResult,
  setSelectedCategory,
  setActiveTag,
  setSearchTerm,
  setKioskLanguage,
  resetKioskSession,
} = kioskSlice.actions;

export default kioskSlice.reducer;
